import React, { useCallback, useState, RefObject, useEffect, useRef,useLayoutEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, {
  EventResizeDoneArg,
} from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import {
  EventDropArg,
  EventApi,
  EventClickArg,
  EventMountArg,
  EventHoveringArg,
  EventContentArg,
} from '@fullcalendar/core/index.js';
import { EventReceiveArg } from '@fullcalendar/interaction';

import { getCalendarWorks, updateWork } from '../../api/works';
import {
  eventCSSClassByStatus,
  eventEditable,
  lateEvent,
} from '../shared/eventStyle';
import EditWorkDuration from './EditWorkDuration';
import { EditWorkDurationArg, LeftSectionArg } from '../types';

interface WorksFullCalendarProps {
  calendarRef: RefObject<any>;
  userTimeZone: string;
  userCanUpdateWork: boolean;
  leftSection: LeftSectionArg;
}

interface ModalState{
  isOpen: boolean;
  event: any;
  position: {
    top: number;
    left: number;
  };
  html: string;
}

const WorksFullCalendar: React.FC<WorksFullCalendarProps> = ({
  calendarRef,
  userTimeZone,
  userCanUpdateWork,
  leftSection,
}) => {
  const baseEditWorkObject: EditWorkDurationArg = {
    calendarInfo: null,
    hasEndTime: false,
    oldEventStart: null,
    oldEventEnd: null,
    userTimeZone: userTimeZone,
    successCallback: () => {},
    failureCallback: () => {},
  };

  const [editWorkObject, setEditWorkObject] = useState({
    ...baseEditWorkObject,
  });

  const fetchEvents = useCallback(async (info) => {
    const startDate = info.start.toLocaleDateString('pt-BR');
    const endDate = info.end.toLocaleDateString('pt-BR');

    const urlParams = new URLSearchParams(window.location.search);

    urlParams.append(
      'calendar_scheduled_range_time',
      `${startDate} - ${endDate}`
    );
    urlParams.append('without_pagination', 'true');

    const jsonData = await getCalendarWorks({ urlParams: urlParams }).catch(
      (e) => {
        alert(e.message);
        return [];
      }
    );

    return jsonData.map((el) => {
      return {
        id: el.id,
        title: el.title,
        start: el['start_time'],
        end: el['end_time'],
        url: `/works/${el.id}`,
        extendedProps: {
          status: el.status,
        },
        editable: userCanUpdateWork && eventEditable(el.status),
        classNames: [eventCSSClassByStatus(el.status), `work-${el.id}`],
      };
    });
  }, []);

  const createWorkUpdateBody = (workEvent: EventApi) => {
    const startTime = workEvent.startStr;
    let endTime: string | null = null;

    if (workEvent.end) {
      endTime = workEvent.endStr;
    }

    return {
      work: {
        start_time: startTime,
        end_time: endTime,
      },
    };
  };

  const eventClassNames = (arg: EventContentArg) => {
    const { isStart, event, view } = arg;
    const { id, extendedProps, endStr } = event;
    const status = extendedProps['status'];
    const classNames = [eventCSSClassByStatus(status), `work-${id}`];

    if (lateEvent(status, endStr)) {
      classNames.push('late-event');
    }

    if (view.type !== 'timeGridDay' && !isStart) {
      classNames.push('repeated-event');
    }

    return classNames;
  };

  const modalRef = useRef<HTMLDivElement>(null);

  const [modalData, setModalData] = useState<ModalState>({
    isOpen: false,
    event: null,
    position: { top: 0, left: 0 },
    html: ''
  })

  const eventClick = async (info: EventClickArg) => {
    info.jsEvent.preventDefault();

    if (modalData.isOpen && modalData.event && modalData.event.id === info.event.id) {
      setModalData(prev => ({ ...prev, isOpen: false }));
      return;
    }
    
    const eventRect = info.el.getBoundingClientRect();
    const workId = info.event.id;
    
    const containerRect = info.el.closest('.fc') 
      ? info.el.closest('.fc')!.getBoundingClientRect() 
      : { top: 0, left: 0 };
    const containerScrollTop = info.el.closest('.fc-scroller') 
      ? info.el.closest('.fc-scroller')!.scrollTop 
      : 0;

    try {
      const response = await fetch(`/works/${workId}/card_modal`);
      const htmlContent = await response.text();

      setModalData({
        event: info.event,
        position: {
          top: eventRect.bottom - containerRect.top + containerScrollTop,
          left: eventRect.right - containerRect.left - 80, 
        },
        isOpen: true,
        html: htmlContent
      });
    } catch (error) {
      console.error("Erro ao buscar detalhes do card", error);
    }
  };
    
  useLayoutEffect(() => {
    if (modalData.isOpen && modalRef.current) {
      const modalRect = modalRef.current.getBoundingClientRect();
      const securityPadding = 30; 
      
      let adjustedLeft = modalData.position.left;
      let adjustedTop = modalData.position.top;
      let changed = false;

      if (modalRect.right > window.innerWidth) {
        const excess = modalRect.right - window.innerWidth;
        adjustedLeft = modalData.position.left - excess - securityPadding;
        changed = true;
      }

      if (modalRect.bottom > window.innerHeight) {
        const excessY = modalRect.bottom - window.innerHeight;
        adjustedTop = modalData.position.top - excessY - securityPadding;
        changed = true;
      }

      if (changed) {
        setModalData(prev => ({
          ...prev,
          position: {
            top: adjustedTop,
            left: adjustedLeft
          }
        }));
      }
    }
  }, [modalData.isOpen, modalData.html]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!modalData.isOpen) return;

      const target = event.target as HTMLElement;
      const clickedOverlay = target === modalRef.current;
      const clickedInsideCard = modalRef.current && modalRef.current.contains(target) && !clickedOverlay;
      const clickedAnEvent = target.closest('.fc-event');

      if (clickedOverlay || (!clickedInsideCard && !clickedAnEvent)) {
        setModalData(prev => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalData.isOpen]);

  useEffect(() => {
  if (modalData.isOpen && modalData.html) {
    const timer = setTimeout(() => {
      if (typeof (window as any).componentHandler !== 'undefined') {
        (window as any).componentHandler.upgradeAllRegistered();
      }
    }, 100);

    return () => clearTimeout(timer);
  }
}, [modalData.isOpen, modalData.html]);
  

  const eventResize = (info: EventResizeDoneArg) => {
    const body = createWorkUpdateBody(info.event);

    updateWork({ id: info.event.id, body: body }).catch((e) => {
      info.revert();
      alert(e.message);
    });
  };

  const eventDrop = (info: EventDropArg) => {
    const {
      type: viewType,
      currentStart,
      currentEnd,
    } = calendarRef.current.getApi().view;

    const currentStartNumber = currentStart.getTime();
    const currentEndNumber = currentEnd.getTime();
    const eventStartNumber = info.event.start?.getTime() || 0;
    const eventEndNumber = info.event.end?.getTime() || 0;

    if (
      viewType === 'timeGridDay' &&
      eventStartNumber >= currentStartNumber &&
      eventEndNumber <= currentEndNumber
    ) {
      const body = createWorkUpdateBody(info.event);
      updateWork({ id: info.event.id, body: body }).catch((e) => {
        info.revert();
        alert(e.message);
      });

      return;
    }

    setEditWorkObject({
      calendarInfo: info,
      hasEndTime: true,
      oldEventStart: info.oldEvent.startStr,
      oldEventEnd: info.oldEvent.endStr,
      userTimeZone: userTimeZone,
      successCallback: (newStart, newEnd) => {
        const event = info.event;

        const newEvent = {
          id: event.id,
          title: event.title,
          start: newStart,
          end: newEnd,
          allDay: false,
          url: event.url,
          extendedProps: { ...event.extendedProps },
          classNames: [...event.classNames],
        };

        calendarRef.current.getApi().addEvent(newEvent, true);
        info.event.remove();

        setEditWorkObject({ ...baseEditWorkObject });
      },
      failureCallback: () => {
        info.revert();
        setEditWorkObject({ ...baseEditWorkObject });
      },
    });
  };

  const eventReceive = (info: EventReceiveArg) => {
    const event = info.event;
    let hasEndTime = false;

    const viewType = calendarRef.current.getApi().view.type;

    if (viewType === 'timeGridDay') {
      hasEndTime = true;
    }

    setEditWorkObject({
      calendarInfo: info,
      hasEndTime: hasEndTime,
      oldEventStart: event.extendedProps['originalStart'],
      oldEventEnd: event.extendedProps['originalEnd'],
      userTimeZone: userTimeZone,
      successCallback: (newStart, newEnd) => {
        const status = event.extendedProps['status'];

        const newEvent = {
          id: event.id,
          title: event.title,
          start: newStart,
          end: newEnd,
          allDay: false,
          url: event.url,
          extendedProps: {
            status: status,
          },
          classNames: [...event.classNames],
        };

        (info.draggedEl.parentNode as HTMLElement).style.display = 'none';

        calendarRef.current.getApi().addEvent(newEvent, true);
        info.event.remove();
        setEditWorkObject({ ...baseEditWorkObject });
      },
      failureCallback: () => {
        info.revert();
        setEditWorkObject({ ...baseEditWorkObject });
      },
    });
  };

  const eventDidMount = (info: EventMountArg) => {
    info.el.title = info.event.title;
  };

  const eventMouseEnter = (info: EventHoveringArg) => {
    const eventId = info.event.id;
    document.querySelectorAll(`.work-${eventId}`).forEach((el) => {
      el.classList.add('hover');
    });
  };

  const eventMouseLeave = (info: EventHoveringArg) => {
    const eventId = info.event.id;
    document.querySelectorAll(`.work-${eventId}`).forEach((el) => {
      el.classList.remove('hover');
    });
  };

  const defineInitialSettings = () => {
    const formatDateString = (date: string) => {
      // DD/MM/YYYY to YYYY-MM-DD
      const dateArr = date.split('/');
      return [dateArr[2], dateArr[1], dateArr[0]].join('-');
    };

    const dateDifferenceInDays = (startDate: number, endDate: number) =>
      (endDate - startDate) / 86_400_000;

    const urlParams = new URLSearchParams(window.location.search);
    const schedueldRangeTimeParam = urlParams.get(
      'calendar_scheduled_range_time'
    );

    let initialDate: string | undefined = undefined;
    let initialView = 'dayGridMonth';
    let leftHeaderToolbar = 'prev,next today';

    if (schedueldRangeTimeParam) {
      const rangeDate = schedueldRangeTimeParam.split('-');

      const startDate = formatDateString(rangeDate[0].trim());
      const endDate = formatDateString(rangeDate[1].trim());
      const differenceDays = dateDifferenceInDays(
        Date.parse(startDate),
        Date.parse(endDate)
      );

      initialDate = startDate;

      if (differenceDays === 0) {
        initialView = 'timeGridDay';
      }
    }

    if (userCanUpdateWork && !leftSection.leftSectionVisible) {
      leftHeaderToolbar = 'leftSectionButton prev,next today';
    }

    return {
      initialView: initialView,
      initialDate: initialDate,
      leftHeaderToolbar: leftHeaderToolbar,
    };
  };

  const initialSettings = defineInitialSettings();

  return (
    <div className="calendar" style={{ position: 'relative', width: '100%' }}>
      <FullCalendar
        ref={calendarRef}
        timeZone={userTimeZone}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={initialSettings.initialView}
        initialDate={initialSettings.initialDate}
        eventTimeFormat={{
          // like '14:00'
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
        }}
        views={{
          timeGridDay: {
            eventMaxStack: 6,
            slotEventOverlap: false,
          },
        }}
        headerToolbar={{
          left: initialSettings.leftHeaderToolbar,
          center: 'title',
          right: 'dayGridMonth,dayGridWeek,timeGridDay',
        }}
        customButtons={{
          leftSectionButton: {
            text: 'A planejar',
            icon: 'calendar-material-symbol-left-button',
            click: () => {
              leftSection.updateLeftSectionVisibility();
            },
          },
        }}
        selectable={true}
        editable={userCanUpdateWork}
        allDaySlot={false}
        dayMaxEventRows={true}
        navLinks={true}
        defaultAllDay={false}
        forceEventDuration={true}
        locale={ptBrLocale}
        events={fetchEvents}
        eventClassNames={eventClassNames}
        eventClick={eventClick}
        eventDrop={eventDrop}
        eventResize={eventResize}
        eventReceive={eventReceive}
        eventDidMount={eventDidMount}
        eventMouseEnter={eventMouseEnter}
        eventMouseLeave={eventMouseLeave}
        eventDragStart={()=>{setModalData(prev => ({ ...prev, isOpen: false }))}}
      />

      <EditWorkDuration editWorkObject={editWorkObject} />
      {modalData.isOpen && (
        <div 
          ref={modalRef}
          className='calendar-modal'
          style={{
            position: 'absolute',
            top: `${modalData.position.top}px`,
            left: `${modalData.position.left}px`,
            zIndex: 9999,
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: modalData.html }} />
        </div>
      )}
    </div>
  );
};

export default WorksFullCalendar;
